import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import { client, scheduleRogerExistingPatient } from '../../services/api';
import type { RogerOnboardPatientResponse } from '../../services/api/types.gen';

interface UseRogerSchedulingOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
}

interface UseRogerSchedulingReturn {
  schedulingInRoger: boolean;
  rogerSchedulingResult: RogerOnboardPatientResponse | null;
  handleScheduleInRoger: () => Promise<void>;
}

/**
 * Hook for scheduling a SOC or ROC visit on an existing Roger patient.
 *
 * Unlike `useRogerCreation`, this does NOT create a new patient. It calls the
 * `schedule_roger_existing_patient` endpoint, which creates the Admission / Episode / Visit
 * records on the intake's matched patient based on `kinnser_visit_type` and
 * `kinnser_creation_type` stored in intake.fields (these keys are shared by
 * both Kinnser and Roger scheduling flows; the `kinnser_` prefix is historical).
 */
export function useRogerScheduling({
  intakeId,
  intakeStatus,
}: UseRogerSchedulingOptions): UseRogerSchedulingReturn {
  const [schedulingInRoger, setSchedulingInRoger] = useState(false);
  const [rogerSchedulingResult, setRogerSchedulingResult] =
    useState<RogerOnboardPatientResponse | null>(null);

  const clearRogerSchedulingResult = useCallback(() => {
    setRogerSchedulingResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearRogerSchedulingResult
  );

  const handleScheduleInRoger = useCallback(async () => {
    if (!intakeId) return;

    setSchedulingInRoger(true);
    setRogerSchedulingResult(null);

    try {
      const response = await scheduleRogerExistingPatient({
        client,
        path: { intake_id: intakeId },
        throwOnError: true,
      });

      if (response.data) {
        setRogerSchedulingResult(response.data);
      }
    } catch (error) {
      console.error('Error scheduling visit on existing Roger patient:', error);
      let errorMessage = 'An unexpected error occurred. Please try again.';

      const axiosError = error as {
        response?: { data?: { detail?: string | unknown[] } };
        message?: string;
      };

      if (axiosError.response?.data?.detail) {
        const detail = axiosError.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail
            .map((err: unknown) => {
              const validationError = err as { msg?: string; loc?: string[] };
              const field = validationError.loc?.join('.') || 'Field';
              return `${field}: ${validationError.msg || 'Invalid value'}`;
            })
            .join('; ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }

      setRogerSchedulingResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setSchedulingInRoger(false);
    }
  }, [intakeId]);

  return {
    schedulingInRoger,
    rogerSchedulingResult,
    handleScheduleInRoger,
  };
}
